import React, { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Layouts
import { PublicLayout } from './components/Layout';
import { DashboardLayout } from './components/DashboardLayout';

// Components
import { SplashScreen } from './components/SplashScreen';

// Public Pages
import { Landing } from './components/public/Landing';
import { VerificationResult } from './components/public/VerificationResult';
import { Login } from './components/auth/Login';
import { Signup } from './components/auth/Signup';

// Dashboard Pages
import { DashboardHome } from './components/dashboard/DashboardHome';
import { Certificates } from './components/dashboard/Certificates';
import { Students } from './components/dashboard/Students';
import { Admins } from './components/dashboard/Admins';
import { Profile } from './components/dashboard/Profile';

// Types
import { UserProfile, UserRole } from './types';

const App: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Restore session and theme, then finish splash
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('app_user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      const storedTheme = localStorage.getItem('app_dark_mode');
      if (storedTheme) {
        setIsDarkMode(storedTheme === 'true');
      }
    } catch {}
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Persist session and theme changes
  useEffect(() => {
    try {
      if (user) localStorage.setItem('app_user', JSON.stringify(user));
      else localStorage.removeItem('app_user');
    } catch {}
  }, [user]);

  useEffect(() => {
    try { localStorage.setItem('app_dark_mode', String(isDarkMode)); } catch {}
  }, [isDarkMode]);

  if (isLoading) {
    return <SplashScreen />;
  }

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleLogin = (email: string) => {
    // Logic: If email contains 'super', assign SuperAdmin role, else Admin.
    const role: UserRole = email.toLowerCase().includes('super') ? 'SuperAdmin' : 'Admin';
    const namePart = email.split('@')[0];
    const formattedName = namePart.charAt(0).toUpperCase() + namePart.slice(1).replace(/[._]/g, ' ');

    const newUser: UserProfile = {
      name: formattedName || 'System User',
      email: email,
      role: role,
      location: 'Lagos, Nigeria'
    };
    setUser(newUser);
  };

  const handleLogout = () => {
    setUser(null);
    try { localStorage.removeItem('app_token'); } catch {}
  };

  return (
    <BrowserRouter>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar theme={isDarkMode ? 'dark' : 'light'} />
      <Routes>
        {/* Public Routes with Main Layout */}
        <Route path="/" element={<PublicLayout><Landing /></PublicLayout>} />
        <Route path="/verify/:id" element={<PublicLayout><VerificationResult /></PublicLayout>} />
        <Route path="/verify" element={<PublicLayout><VerificationResult /></PublicLayout>} />
        <Route path="/login" element={<PublicLayout><Login onLogin={handleLogin} /></PublicLayout>} />
        <Route path="/signup" element={<PublicLayout><Signup onSignup={handleLogin} /></PublicLayout>} />

        {/* Protected Dashboard Routes */}
        <Route 
          path="/dashboard" 
          element={
            user ? (
              <DashboardLayout 
                isDarkMode={isDarkMode} 
                toggleTheme={toggleTheme} 
                user={user} 
                onLogout={handleLogout} 
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="certificates" element={<Certificates />} />
          <Route path="students" element={<Students />} />
          <Route path="admins" element={<Admins />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
